import * as React from 'react';

import moment from 'moment';

import type {ExternalServiceType} from 'nutshell-graphql-types';

import {appHistory} from '../../../../../history';
import {trackLoggingFlowInitiated} from '../../../../../ui/external-meetings/tables/helpers';
import {useOpenableModal, OpenableModals} from '../../../../../ui/openable-modals';

import {CheckIcon} from '../icon';
import {ListRowActivity} from '../list-row/list-row-activity';
import {formatDateForOverdueActivity} from '../utils/activity-helpers';

import {ActivityItem} from './activity-item';
import {getActionOptionsForEntities} from './get-action-options-for-entities';

type Props = {
    userId: string;
    style?: any;
    // Shows the date next to the time, instead of just showing the time
    showRichTimestamp?: boolean;
    activity: {
        id: string;
        type: string;
        name: string;
        htmlUrl: string;
        startTime: number;
        endTime: number;
        isLogged: boolean;
        isAllDay: boolean;
        isLastDay?: boolean; // Comes from redux selector.  Missing in graphql, which is used in sidefire calendar,
        isOverdue: boolean;
        isFlagged?: boolean;
        agenda: string;
        participants: any[];
        showTranscribeLoggingFlow: boolean;
        externalMeetingServiceType: ExternalServiceType | null | undefined;
        isPrivate?: boolean;
    };
    hasActions?: boolean;
    hasBorders?: boolean;
    hideActionMenu?: boolean;
    maxParticipantsDisplayed?: number;
    onStartEmailToParticipant?: (entityId: string) => any;
    onStartCallToParticipant?: (arg1: {
        entityId: string;
        phoneNumber: {
            E164: string;
            numberFormatted: string;
        };
        anchorElementId: string;
    }) => void;
};

export function ActivityListItem(props: Props) {
    const {hasActions = true} = props;

    const {onOpen} = useOpenableModal(OpenableModals.ACTIVITY_LOG_MODAL, {
        activityId: props.activity.id,
    });

    // activities can be multi-day and show as separate items in the timeline in
    // that case, so the start time and id ensure that the anchor element id is
    // unique for each activity.
    const anchorElementId = `${props.activity.id}-${props.activity.startTime}`;

    const getFormattedTimestamp = () => {
        if ('isLastDay' in props.activity && props.activity.isLastDay) {
            return `Ends ${moment.unix(props.activity.endTime).format('h:mma')}`;
        } else if (props.activity.startTime && !props.activity.isAllDay) {
            return moment.unix(props.activity.startTime).format('h:mma');
        } else {
            return 'All day';
        }
    };

    const handleStartCallToParticipant = ({
        entityId,
        phoneNumber,
    }: {
        entityId: string;
        phoneNumber: {
            E164: string;
            numberFormatted: string;
        };
    }) => {
        if (props.onStartCallToParticipant) {
            props.onStartCallToParticipant({
                entityId,
                phoneNumber,
                anchorElementId,
            });
        }
    };

    // @ts-expect-error
    let options = [];
    if (props.onStartEmailToParticipant && props.onStartCallToParticipant) {
        options = getActionOptionsForEntities(props.activity.participants, {
            email: props.onStartEmailToParticipant,
            call: handleStartCallToParticipant,
        });
    }

    const handleView = () => {
        if (
            (NutClientConfig.hasNewActivityModalFF && props.activity.isLogged) ||
            NutClientConfig.hasNewActivityModalEverywhereFF
        ) {
            appHistory.replace({
                ...appHistory.location,
                hash: `/${props.activity.id}`,
                state: {modal: true},
            });

            return;
        }

        if (props.activity.showTranscribeLoggingFlow && props.activity.externalMeetingServiceType) {
            trackLoggingFlowInitiated(props.activity.externalMeetingServiceType, 'dashboard');
            onOpen();
        } else {
            Modal.open({
                url: `/activity/view/${props.activity.id}`,
            });
        }
    };

    const renderSecondLine = () => {
        if (props.activity.isLogged) {
            return (
                <span className='flex align-center gap-4'>
                    <CheckIcon />
                    <span>Logged</span>
                </span>
            );
        } else if (props.activity.isOverdue && props.showRichTimestamp) {
            return (
                <span>{formatDateForOverdueActivity(moment.unix(props.activity.startTime))}</span>
            );
        } else {
            return undefined;
        }
    };

    return (
        <ListRowActivity
            style={props.style}
            onClick={handleView}
            isOverdue={props.activity.isOverdue}
            isCompleted={props.activity.isLogged}
            // @ts-expect-error
            isLogged={props.activity.isLogged}
            // @ts-expect-error
            options={options}
            hasBorders={props.hasBorders}
            hideActionMenu={props.hideActionMenu}
            secondLine={renderSecondLine()}
            formattedTimestamp={getFormattedTimestamp()}
            timestamp={props.activity.startTime}
            anchorElementId={anchorElementId}
        >
            <ActivityItem
                userId={props.userId}
                maxParticipantsDisplayed={props.maxParticipantsDisplayed}
                hasActions={hasActions}
                activity={props.activity}
            />
        </ListRowActivity>
    );
}
